/**
 * FlowNotes API Client - Documents
 * Document CRUD, search, projects, folders, and slug utilities
 */

import { fetchWithAuth, API_BASE_URL, UUID_REGEX } from './core';
import {
  documentUuidFromPathOrId,
  resolveStoragePathForFetch,
  storagePathProbeCandidates,
  type WorkspaceRouteInfo,
} from '@/lib/app-routes';

export interface ChangedLines {
  added: number[]
  removed: number[]
  modified: number[]
  changed_at?: string
  changed_by?: string
  change_status?: 'pending' | 'accepted' | 'rejected' | 'auto'
  requires_acceptance?: boolean
  accepted_at?: string | null
  conversation_id?: string | null
}

export interface Document {
  id: string;
  title: string;
  type:
    | 'work_spec'
    | 'note'
    | 'voice_memo'
    | 'meeting'
    | 'todo'
    | 'proposal'
    | 'email'
    | 'incident'
    | 'personal'
    | 'project'
    | 'html'
    | 'csv'
    | 'audio'
    | 'image'
    | 'flow'
    | 'skill'
    | 'quiz';
  excerpt: string;
  content?: string;
  updatedAt: string;
  /** ISO timestamp for client-side merge sort with explorer folders (GET /api/documents list). */
  sortUpdatedAt?: string | null;
  updatedBy: string;
  /** User id for PersonLink (list / search / recent). */
  updatedByUserId?: string | null;
  tags: string[];
  /** Lines in document body (list endpoint). */
  lineCount?: number;
  projectName?: string;
  projectId?: string;
  status?: string;
  filePath?: string;
  /** Some GET handlers return snake_case */
  file_path?: string;
  createdBy?: string;
  createdAt?: string;
  frontmatter?: Record<string, any>;
  changedLines?: ChangedLines | null;
  changed_lines?: ChangedLines | null;
  file_modified_at?: string | null;
  /** CamelCase alias some handlers return */
  fileModifiedAt?: string | null;
}

export interface Project {
  id: string;
  name: string;
  slug: string;
  description?: string;
  documentCount: number;
  organizationName?: string;
  createdAt?: string;
}

export interface ProjectHierarchy {
  'Hop Studios': Project[];
  'Clients': Project[];
  'Personal': Project[];
}

export interface DocumentListResponse {
  documents: Document[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  debug?: {
    folder?: string;
    totalCount?: number;
    documentsReturned?: number;
    countQuery?: string;
    countParams?: any[];
    countQueryHasFolder?: boolean;
    mainQueryHasFolder?: boolean;
    countConditionsCount?: number;
    countConditions?: string[];
  };
}

export interface SearchResult {
  id: string;
  title: string;
  type: string;
  excerpt: string;
  tags: string[];
  matchScore: number;
  projectName?: string;
  updatedBy: string;
  updatedByUserId?: string | null;
  updatedAt: string | null;
  filePath?: string;
}

export interface SearchResponse {
  results: SearchResult[];
  query: string;
  count: number;
  total?: number;
  exactPathMatch?: boolean;
  similarityThreshold?: number;
  /** Highest cosine similarity in scope, even when it is under the cutoff. */
  bestSimilarity?: number | null;
  scope?: {
    folderPath?: string;
    filePath?: string;
  };
}

/**
 * Fetch all documents with optional filters
 */
export async function getDocuments(options: {
  type?: string;
  project?: string;
  folder?: string;
  author?: string;
  updatedBy?: string;
  limit?: number;
  offset?: number;
  sort?: 'recent' | 'oldest' | 'title-asc' | 'title-desc';
  tags?: string[];
  includeArchived?: boolean;
} = {}): Promise<DocumentListResponse> {
  const params = new URLSearchParams();

  if (options.type) params.append('type', options.type);
  if (options.project) params.append('project', options.project);
  if (options.folder) params.append('folder', options.folder);
  if (options.author) params.append('author', options.author);
  if (options.updatedBy) params.append('updatedBy', options.updatedBy);
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());
  if (options.sort) params.append('sort', options.sort);
  if (options.tags) {
    options.tags.forEach(tag => params.append('tags', tag));
  }
  if (options.includeArchived) params.append('includeArchived', 'true');

  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents?${params}`);

  if (!response.ok) {
    const err = await response.json().catch(() => ({} as { error?: string; message?: string }));
    const st = (response.statusText && response.statusText.trim()) || '';
    const detail =
      (typeof err.message === 'string' && err.message.trim()) ||
      (typeof err.error === 'string' && err.error.trim()) ||
      st ||
      `HTTP ${response.status} (no error body; often DB pooler timeout or API overload)`;
    throw new Error(`Failed to fetch documents (${response.status}): ${detail}`);
  }

  return response.json();
}

export type NavigationResolution =
  | {
      kind: 'document'
      id: string
      title: string
      type: string
      filePath: string
      organizationId?: string
    }
  | { kind: 'folder'; path: string }
  | { kind: 'missing'; path?: string }

/**
 * One request: extensionless browser path is a document or a folder.
 * Used before painting either view on a direct URL.
 */
export async function resolveNavigationTarget(
  storagePath: string,
): Promise<NavigationResolution> {
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/documents/resolve-nav?path=${encodeURIComponent(storagePath)}`,
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({} as { error?: string }))
    throw new Error(err.error || `resolve-nav failed (${response.status})`)
  }
  const body = (await response.json()) as {
    kind?: string
    id?: string
    title?: string
    type?: string
    filePath?: string
    file_path?: string
    organizationId?: string
    organization_id?: string
    path?: string
  }
  if (body.kind === 'document' && body.id) {
    return {
      kind: 'document',
      id: body.id,
      title: body.title || body.filePath || body.file_path || body.id,
      type: body.type || 'note',
      filePath: body.filePath || body.file_path || storagePath,
      organizationId: body.organizationId || body.organization_id,
    }
  }
  if (body.kind === 'folder') {
    return { kind: 'folder', path: body.path || storagePath }
  }
  return { kind: 'missing', path: body.path || storagePath }
}

/**
 * Fetch a single document by ID
 */
export async function getDocument(id: string): Promise<Document> {
  const uuidFromPath = documentUuidFromPathOrId(String(id).trim());
  const isUuid = Boolean(uuidFromPath) || UUID_REGEX.test(String(id).trim());
  const fetchId = uuidFromPath || String(id).trim();
  const useByPath =
    !isUuid &&
    (id.includes('/') ||
      id.endsWith('.md') ||
      id.toLowerCase().endsWith('.html') ||
      /^\d+-/.test(id));

  if (useByPath) {
    const candidates = storagePathProbeCandidates(id.replace(/^\/+/, ''));
    let lastError = 'Document not found';
    for (const candidate of candidates) {
      const url = `${API_BASE_URL}/api/documents/by-path?path=${encodeURIComponent(candidate)}`;
      const response = await fetchWithAuth(url);
      if (response.ok) {
        return response.json();
      }
      lastError = response.statusText || lastError;
      // Keep probing on 404 (and legacy 403-for-miss) so alternate extensions can win.
      if (response.status !== 404 && response.status !== 403) {
        throw new Error(`Failed to fetch document: ${lastError}`);
      }
    }
    throw new Error(`Failed to fetch document: ${lastError}`);
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/${fetchId}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch document: ${response.statusText}`);
  }

  return response.json();
}

export type ResolveDocumentRowIdOptions = {
  /** When set, slug-shaped browser paths (e.g. flownotes-product/...) map to storage roots before by-path probes. */
  workspaces?: WorkspaceRouteInfo[]
}

/**
 * Resolve a tab key / file path to documents.id for routes that use POST /api/documents/:id/...
 * When tabId is a stale UUID, pass pathFallback (filePath / storage path) to re-resolve via by-path.
 */
export async function resolveDocumentRowId(
  idOrPath: string,
  pathFallback?: string | null,
  options?: ResolveDocumentRowIdOptions,
): Promise<string> {
  const trimmed = String(idOrPath).trim();
  const fallbackPath = pathFallback?.trim() || null;
  const workspaces = options?.workspaces ?? [];

  if (UUID_REGEX.test(trimmed)) {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/${trimmed}`);
    if (response.ok) return trimmed;
    if (response.status === 404 && fallbackPath && fallbackPath !== trimmed) {
      return resolveDocumentRowId(fallbackPath, null, options);
    }
    if (response.status === 404) {
      throw new Error('Document not found');
    }
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || 'Document not found');
  }

  const looksLikePath =
    trimmed.includes('/') ||
    trimmed.endsWith('.md') ||
    trimmed.toLowerCase().endsWith('.html') ||
    /^\d+-/.test(trimmed);
  if (!looksLikePath) return trimmed;

  const storagePath = resolveStoragePathForFetch(trimmed, workspaces);
  const candidates = storagePathProbeCandidates(storagePath.replace(/^\/+/, ''));
  if (fallbackPath && fallbackPath !== trimmed) {
    const fallbackStorage = resolveStoragePathForFetch(fallbackPath, workspaces);
    for (const c of storagePathProbeCandidates(fallbackStorage.replace(/^\/+/, ''))) {
      if (!candidates.includes(c)) candidates.push(c);
    }
  }

  let doc: { id?: string; source?: string; _source?: string; isReadOnly?: boolean; filePath?: string } | null = null;
  for (const candidate of candidates) {
    const url = `${API_BASE_URL}/api/documents/by-path?path=${encodeURIComponent(candidate)}`;
    const response = await fetchWithAuth(url);
    if (response.ok) {
      doc = await response.json();
      break;
    }
    // 403 used to mean "scoped miss" as well as true denial - keep probing candidates.
    if (response.status !== 404 && response.status !== 403) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || 'Document not found');
    }
  }
  if (!doc) {
    throw new Error('Document not found');
  }
  if (!doc?.id) {
    const fromFilesystemOnly =
      doc?.source === 'filesystem' ||
      doc?._source === 'file' ||
      Boolean(doc?.isReadOnly === true && doc?.filePath);
    throw new Error(
      fromFilesystemOnly
        ? 'Cannot pin yet: file is not in the Maater database. Open the doc once or wait for sync, then try again.'
        : 'Document not found'
    );
  }
  return doc.id;
}

/** Resolve tab id + optional storage path before rename/move/pin mutations. */
export async function resolveDocumentIdForMutation(
  tabId: string,
  filePath?: string | null,
): Promise<string> {
  const pathFallback = filePath?.trim() || (tabId.includes('/') ? tabId : null);
  return resolveDocumentRowId(tabId, pathFallback);
}

/** Encode each path segment for /doc/... URLs (never encode the whole path as one segment). */
export function encodeDocumentPathForUrl(contentRelativePath: string): string {
  return String(contentRelativePath)
    .split('/')
    .map((s) => encodeURIComponent(s))
    .join('/');
}

/**
 * Slugify a string for use in filenames
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Generate filename from title
 */
export function generateFilenameFromTitle(title: string): string {
  const slug = slugify(title);
  if (!slug) return 'untitled-note.md';
  return `${slug}.md`;
}

/**
 * True when the file still uses the provisional name from POST /api/documents
 */
export function isAutoSlugUntitledFilename(filePathOrBasename: string): boolean {
  const trimmed = String(filePathOrBasename).trim();
  const base = trimmed.includes('/')
    ? (trimmed.split('/').pop() ?? '')
    : trimmed;
  return /^untitled-note(-\d+)?\.md$/i.test(base);
}

/**
 * Rename a document file
 */
export async function renameDocument(
  id: string,
  filename: string,
  options?: { pathFallback?: string | null },
): Promise<{ success: boolean; document: { id: string; oldPath: string; newPath: string } }> {
  const documentId = await resolveDocumentRowId(id, options?.pathFallback);
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/${documentId}/rename`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ filename }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to rename document: ${response.statusText}`);
  }

  return response.json();
}

/**
 * AI suggest document filename slug (preview only - no prefixes, no .md).
 * Also returns an aligned human-readable `title` when the model provides one.
 * Pass prior slugs in `avoid` to shuffle.
 */
export async function suggestDocumentFilename(
  id: string,
  options?: { avoid?: string[]; pathFallback?: string | null },
): Promise<{
  success: boolean;
  slug: string;
  title?: string;
  message?: string;
  model?: string;
}> {
  const documentId = await resolveDocumentRowId(id, options?.pathFallback);
  const response = await fetchWithAuth(
    `${API_BASE_URL}/api/documents/${documentId}/suggest-filename`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ avoid: options?.avoid ?? [] }),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to suggest filename: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Rename a folder
 */
export async function renameFolder(
  folderPath: string,
  newName: string
): Promise<{ success: boolean; folder: { oldPath: string; newPath: string; documentsUpdated: number } }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/filesystem/folders/${encodeURIComponent(folderPath)}/rename`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ newName }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to rename folder: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a document
 */
export async function updateDocument(
  id: string,
  updates: Partial<Pick<Document, "title" | "content" | "tags" | "status">> & {
    writeToFile?: boolean
    clearChangeMarkers?: boolean
    /** When set, persists to `documents.frontmatter` and is merged on file write */
    frontmatter?: Record<string, unknown>
    /** When true, the API inserts a `document_edits` row for this save (used for version history). Defaults to false server-side. */
    saveEditHistory?: boolean
    /** Attribution for the `document_edits` row when `saveEditHistory` is true. Defaults to 'user' server-side. */
    changedBy?: 'user' | 'ai' | 'system'
    /** Optional context merged into the `document_edits.metadata` JSON, e.g. `{ saveTrigger: 'manual' | 'autosave' | 'restore' }` - see formatEditSourceLabel() in document-version-history.ts. */
    historyMetadata?: Record<string, unknown>
  },
  options?: { pathFallback?: string | null },
): Promise<{ success: boolean; document: Document; changedLines?: ChangedLines | null }> {
  let apiUrl: string
  const trimmed = String(id).trim()
  const pathFallback = options?.pathFallback?.trim() || null

  if (trimmed.includes('/')) {
    apiUrl = `${API_BASE_URL}/api/documents/by-path?path=${encodeURIComponent(trimmed)}`
  } else if (UUID_REGEX.test(trimmed)) {
    const verify = await fetchWithAuth(`${API_BASE_URL}/api/documents/${trimmed}`)
    if (verify.ok) {
      apiUrl = `${API_BASE_URL}/api/documents/${trimmed}`
    } else if (verify.status === 404 && pathFallback) {
      apiUrl = `${API_BASE_URL}/api/documents/by-path?path=${encodeURIComponent(pathFallback)}`
    } else {
      apiUrl = `${API_BASE_URL}/api/documents/${trimmed}`
    }
  } else {
    apiUrl = `${API_BASE_URL}/api/documents/${trimmed}`
  }

  const response = await fetchWithAuth(apiUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error(`Failed to update document: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a document
 */
export async function deleteDocument(id: string): Promise<{ success: boolean }> {
  const isPath = id.includes('/') || id.includes('.') || !UUID_REGEX.test(id)
  const apiUrl = isPath
    ? `${API_BASE_URL}/api/documents/by-path?path=${encodeURIComponent(id)}`
    : `${API_BASE_URL}/api/documents/${id}`

  const response = await fetchWithAuth(apiUrl, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete document: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create an empty folder (DB folders row + optional filesystem mkdir).
 */
export async function createFolder(folderPath: string): Promise<{ success: boolean; path: string }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/folders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: folderPath }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to create folder');
  }
  return data;
}

/**
 * Delete a folder (DB folder rows; optional recursive soft-delete of documents inside).
 */
export async function deleteFolder(
  folderPath: string,
  options?: { recursive?: boolean },
): Promise<{
  success: boolean;
  path: string;
  foldersRemoved: number;
  documentsDeleted: number;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/folders`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      path: folderPath,
      recursive: options?.recursive === true,
    }),
  });
  const data = await response.json();
  if (!response.ok) {
    const parts = [data.error, data.hint].filter(Boolean);
    throw new Error(parts.length > 0 ? parts.join(' ') : 'Failed to delete folder');
  }
  return data;
}

/**
 * Create a new document
 */
export async function createDocument(doc: {
  title?: string;
  content?: string;
  document_type?: string;
  tags?: string[];
  project_id?: string | null;
  folder_path?: string;
  /** Target workspace org id when folder_path is empty or ambiguous (cloud multi-workspace). */
  workspace_id?: string;
  /**
   * Exact content-relative path (e.g. ws/hex/folder/note.md). When set, skips
   * provisional untitled-note.md allocation. Used by local text import/drop.
   */
  file_path?: string;
}): Promise<{ success: boolean; document: Document }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      title: doc.title || 'Untitled Note',
      content: doc.content || '',
      document_type: doc.document_type || 'note',
      tags: doc.tags || [],
      project_id: doc.project_id || null,
      folder_path: doc.folder_path || '',
      ...(doc.workspace_id ? { workspace_id: doc.workspace_id } : {}),
      ...(doc.file_path ? { file_path: doc.file_path } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    const parts = [error.error, error.details].filter(Boolean);
    throw new Error(
      parts.length > 0 ? parts.join(': ') : `Failed to create document: ${response.statusText}`
    );
  }

  return response.json();
}

/**
 * Search documents with full-text search
 */
export async function searchDocuments(
  params: {
    query: string;
    type?: string;
    author?: string;
    updatedBy?: string;
    project?: string;
    dateRange?: string;
    folderPath?: string;
    filePath?: string;
    folderPaths?: string[];
    workspaceIds?: string[];
    limit?: number;
    offset?: number;
    openTabIds?: string[];
    recentlyViewedIds?: string[];
  },
  init?: RequestInit,
): Promise<SearchResponse> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    ...init,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    const parts = [error.error, error.details].filter(Boolean);
    throw new Error(
      parts.length > 0 ? parts.join(': ') : `Search failed: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Semantic search using AI
 */
export async function semanticSearch(
  query: string,
  limit = 10,
  options?: {
    folderPaths?: string[];
    workspaceIds?: string[];
    updatedBy?: string;
    signal?: AbortSignal;
  }
): Promise<SearchResponse> {
  const body: {
    query: string;
    limit: number;
    folderPaths?: string[];
    workspaceIds?: string[];
    updatedBy?: string;
  } = {
    query,
    limit,
  };
  if (options?.folderPaths?.length) body.folderPaths = options.folderPaths;
  if (options?.workspaceIds?.length) body.workspaceIds = options.workspaceIds;
  if (options?.updatedBy) body.updatedBy = options.updatedBy;

  const response = await fetchWithAuth(`${API_BASE_URL}/api/search/semantic`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    signal: options?.signal,
  });

  if (!response.ok) {
    throw new Error(`Semantic search failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Search folders by name/path
 */
export async function searchFolders(
  params: {
    query: string;
    limit?: number;
  },
  init?: RequestInit,
): Promise<{ success: boolean; folders: Array<{ id: string; name: string; path: string; documentCount: number }> }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/filesystem/search-folders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    ...init,
  });

  if (!response.ok) {
    throw new Error(`Folder search failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Search projects
 */
export async function searchProjects(
  query: string,
  limit = 10,
  init?: RequestInit,
): Promise<{ projects: Project[] }> {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString()
  });

  const response = await fetchWithAuth(`${API_BASE_URL}/api/projects/search?${params}`, init);

  if (!response.ok) {
    throw new Error(`Project search failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get document statistics
 */
export async function getDocumentStats(): Promise<{
  total_documents: number;
  total_projects: number;
  work_specs: number;
  notes: number;
  voice_memos: number;
  active_documents: number;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/stats/overview`);

  if (!response.ok) {
    throw new Error(`Failed to fetch stats: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get all projects
 */
export async function getProjects(): Promise<{ projects: Project[] }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/projects`);

  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get project hierarchy for sidebar
 */
export async function getProjectHierarchy(): Promise<{ hierarchy: ProjectHierarchy }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/projects/hierarchy/tree`);

  if (!response.ok) {
    throw new Error(`Failed to fetch project hierarchy: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get documents for a specific project
 */
export async function getProjectDocuments(
  projectId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<{ documents: Document[] }> {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());

  const response = await fetchWithAuth(`${API_BASE_URL}/api/projects/${projectId}/documents?${params}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch project documents: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Resolve a document link to a document ID
 */
export async function resolveDocumentLink(
  href: string,
  currentDocumentPath?: string
): Promise<{
  documentId: string;
  title: string;
  filePath: string;
  resolutionMethod: 'relative_path' | 'filename_search';
} | null> {
  const params = new URLSearchParams();
  params.append('href', href);
  if (currentDocumentPath) {
    params.append('current_path', currentDocumentPath);
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/resolve-link?${params}`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to resolve link: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Restore a soft-deleted document
 */
export async function restoreDocument(
  id: string
): Promise<{ success: boolean; message: string; document: Document }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/${id}/restore`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to restore document: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchDocumentTtsStatus(
  documentId: string,
): Promise<{ configured: boolean; document_type: string | null }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/${documentId}/tts`)
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string }
    throw new Error(body.error || `TTS status failed (${response.status})`)
  }
  return response.json()
}

export async function generateDocumentTts(
  documentId: string,
  voiceId?: string,
): Promise<{
  success: boolean
  attachmentId: string
  audioDocumentId: string
  voice_id: string
  source_hash: string
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/documents/${documentId}/tts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(voiceId ? { voice_id: voiceId } : {}),
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; details?: string }
    throw new Error(body.error || body.details || `TTS failed (${response.status})`)
  }
  return response.json()
}
